import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import IconCancel from '@material-ui/icons/Cancel'
import Notes from '@material-ui/icons/Notes'
import { useEffect, useState } from 'react'
import { FormWithRedirect, required, SaveButton, TextInput, useNotify } from 'react-admin'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { InlineSpinner } from 'shared/components/Spinner'

import { createNote, deleteNote, getNotes, updateNote } from 'api/notes'
import { captureExceptionIfProduction } from 'helpers/utils'
import { CalloutField } from '../Fields'
import Note from '../Notes/Note'

const styles = {
  notesBorder: {
    display: 'flex',
    border: '1px solid #E5E5E5',
    borderRadius: '3px',
    padding: '0 16px',
    minHeight: '80px',
    overflow: 'auto',
  },
}

export function NoteModal({
  handleClose,
  showDialog,
  setShowDialog,
  loading,
  setLoading,
  noteToEdit,
  parentName,
  parentId,
  fetchNotesApi,
  setNotesList,
}: any) {
  const notify = useNotify()

  const handleSave = async (values: any) => {
    setLoading(true)
    const noteApi = noteToEdit ? updateNote : createNote
    noteApi(parentName, parentId, values.text, noteToEdit?.id)
      .then(() => fetchNotesApi(parentName, parentId))
      .then((notesList: any) => {
        setNotesList(notesList?.results ? notesList.results : notesList)
        setLoading(false)
        setShowDialog(false)
        notify(noteToEdit ? 'Note Created!' : 'Note Updated!', { type: 'info' })
      })
      .catch((error: any) => {
        notify(error.message, { type: 'error' })
        captureExceptionIfProduction(error)
        setLoading(false)
        setShowDialog(false)
      })
  }

  return (
    <Dialog fullWidth open={showDialog} onClose={handleClose} aria-label="Note Modal">
      <DialogTitle>{noteToEdit ? 'Edit Note' : 'Add Note'}</DialogTitle>
      <FormWithRedirect
        resource="notes"
        save={handleSave}
        render={({ saving, handleSubmitWithRedirect }) => (
          <>
            <DialogContent>
              <TextInput
                source="text"
                initialValue={noteToEdit ? noteToEdit.text : ''}
                label="Note"
                validate={required()}
                fullWidth
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={loading}>
                <IconCancel />
              </Button>
              <SaveButton
                handleSubmitWithRedirect={handleSubmitWithRedirect}
                saving={saving}
                disabled={loading}
              />
            </DialogActions>
          </>
        )}
      />
    </Dialog>
  )
}

const NotesList = (props: any) => {
  const {
    buttonClass,
    classes,
    parentId,
    parentName,
    maxHeight = '205px',
    width = '550px',
    onNotesChange,
    loadingComponent = (
      <FlexBox alignItems="center" justifyContent="center">
        <InlineSpinner mt={5} mb={5} />
      </FlexBox>
    ),
  } = props
  const note_key_parent = parentName === 'jobs' ? 'job' : 'load'
  const [loading, setLoading] = useState(false)
  const [notesList, setNotesList] = useState<any>(null)
  const [error, setError] = useState<any>(null)
  const notify = useNotify()

  /** api request to get the notes associate with the parent */
  useEffect(() => {
    setLoading(true)
    getNotes(parentName, parentId)
      .then((response) => {
        setLoading(false)
        setNotesList(response.results)
      })
      .catch((error) => {
        setLoading(false)
        setError('Failed to load notes: ' + error.message)
        captureExceptionIfProduction(error)
      })
  }, [parentId, parentName, note_key_parent, setLoading, setError])

  useEffect(() => {
    onNotesChange?.(notesList)
  }, [notesList, onNotesChange])

  /** state related to add/edit note modal */
  const [showDialog, setShowDialog] = useState(false)
  const [loadingDialog, setLoadingDialog] = useState(false)
  const [noteToEdit, setNoteToEdit] = useState(null)
  const handleModalOpen = (note: any) => {
    setNoteToEdit(note)
    setShowDialog(true)
  }
  const handleModalClose = () => {
    setShowDialog(false)
    setNoteToEdit(null)
  }

  const handleNoteDelete = async (note: any) => {
    return deleteNote(parentName, parentId, note.id)
      .then(() => {
        const newNotesList = notesList.filter((obj: any) => obj.id !== note.id)
        setNotesList(newNotesList)
        notify('Note deleted!', { type: 'info' })
      })
      .catch((error) => {
        notify(error.message, { type: 'error' })
        captureExceptionIfProduction(error)
      })
  }

  return loading ? (
    loadingComponent
  ) : error ? (
    <Typography variant="body1">{error}</Typography>
  ) : notesList ? (
    <div>
      <Grid item container direction="row" alignItems="center" spacing={5}>
        <Grid item>
          <CalloutField icon={Notes} label={`Internal Notes (${notesList.length})`} />
        </Grid>
        <Grid item>
          <Button
            className={buttonClass}
            variant="text"
            color="primary"
            onClick={() => {
              handleModalOpen(null)
            }}
          >
            Add Note
          </Button>
          <NoteModal
            showDialog={showDialog}
            setShowDialog={setShowDialog}
            handleClose={handleModalClose}
            setNotesList={setNotesList}
            loading={loadingDialog}
            setLoading={setLoadingDialog}
            noteToEdit={noteToEdit}
            parentName={parentName}
            parentId={parentId}
            fetchNotesApi={getNotes}
          />
        </Grid>
      </Grid>
      <Grid
        className={`${classes.notesBorder} notes-wrap`}
        item
        container
        direction="row"
        style={{ maxHeight, width }}
      >
        {notesList &&
          notesList.map((note: any) => (
            <Note
              key={`${note_key_parent}-note-${note.id}`}
              note={note}
              className={classes.noteBorder}
              handleEditModalOpen={handleModalOpen}
              handleNoteDelete={handleNoteDelete}
              setNotesList={setNotesList}
              parentName={parentName}
              parentId={parentId}
              notesList={notesList}
              fetchNotesApi={getNotes}
            />
          ))}
      </Grid>
    </div>
  ) : null
}

export default withStyles(styles)(NotesList)
